import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Button } from 'antd'
import type { FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'

import B2becButton from '@/components/B2becButton'
import B2BecLink from '@/components/B2BLink'
import BaseBadge from '@/components/BaseBadge'
import {
  BurgerMenuIcon,
  CartIcon,
  ChevronRightIcon,
  CloseIcon,
  SearchIcon,
  UserIcon,
} from '@/components/Icons'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import { selectTotalQuantity } from '@/store/selectors/cartSelector'
import { selectAllowDirectShopCheckout, selectLogo } from '@/store/selectors/configSelector'
import {
  selectCustomerNumberForTechnicalUser,
  selectHasSessionBasedShoppingCart,
  selectIsProcurementTechnicalUserCxml,
} from '@/store/selectors/userSelector'
import { authenticationActions } from '@/store/slices/authenticationSlice'
import {
  selectCustomerAddressPartnerNumber,
  selectIsFetchingCustomerAddress,
} from '@/store/slices/customerSlice'
import { setSearchTerm } from '@/store/slices/searchSlice'
import useDeviceDetect from '@/hooks/useDeviceDetect'
import { usePunchoutContext } from '@/contexts/punchout/usePunchoutContext'
import { useSearchContext } from '@/contexts/search/useSearchContext'
import PermissionWrapper from '@/HOCs/permissionWrapper'
import { MODULE_PROFILE_RIGHTS, MODULE_SHOP_NAVIGATION_RIGHTS } from '@/libs/constants/modulerights'

import HeaderMenu from '../HeaderMenu'

/**
 * User information section with profile details and logout option
 */
interface UserInfoSectionProps {
  isFetching?: boolean
}

const UserInfoSection: FC<React.PropsWithChildren<UserInfoSectionProps>> = ({
  isFetching = false,
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  // Todo: Refactor later,
  // Todo: partnerNumber is for Customer
  // Todo: customerNumber is for Technical
  const partnerNumber = useAppSelector(selectCustomerAddressPartnerNumber)
  const customerNumber = useAppSelector(selectCustomerNumberForTechnicalUser)

  const userIdentifier = partnerNumber || customerNumber || 'N/A'

  const handleLogout = () => {
    dispatch(authenticationActions.signOut({ navigate }))
  }

  return (
    <>
      <UserIcon className="header__user-section__button__icon" />
      <div className="header__user-section__user-menu">
        <div className="header__user-section__logout-wrapper">
          <h4>{t('miniProfile.myId')}</h4>
          <span className="header__user-section__user-name">
            {isFetching
              ? (
                <SkeletonTheme baseColor="#f8f8f8" highlightColor="#fff">
                  <Skeleton />
                </SkeletonTheme>
              )
              : userIdentifier}
          </span>

          <p className="link-to-profile">
            <PermissionWrapper permission={MODULE_PROFILE_RIGHTS.VIEW_PROFILE}>
              <B2BecLink to="/profile">
                <ChevronRightIcon />
                <span>{t('miniProfile.myProfile')}</span>
              </B2BecLink>
            </PermissionWrapper>
          </p>

          <PermissionWrapper
            permission={MODULE_PROFILE_RIGHTS.SEE_LOGOUT_BUTTON}
          >
            <Button
              className="kaercher-button"
              type="primary"
              onClick={handleLogout}
            >
              {t('logout')}
            </Button>
          </PermissionWrapper>
        </div>
      </div>
    </>
  )
}

/**
 * Cart information section with item count badge
 */
interface CartInfoSectionProps {
  totalQuantity?: number
}

const CartInfoSection: FC<React.PropsWithChildren<CartInfoSectionProps>> = ({
  totalQuantity = 0,
}) => {
  return (
    <B2BecLink aria-label="Cart" className="header-nav__main-button" to="/cart">
      <BaseBadge
        isMiniCart
        className="header__user-section__cart-badge"
        count={totalQuantity}
      />
      <CartIcon className="header__user-section__button__icon" />
    </B2BecLink>
  )
}

/**
 * Header logo section component with navigation controls
 */
interface HeaderLogoSectionProps {
  onShowDrawerMobile: () => void
  onHideDrawerMobile: () => void
  inBurgerMenu?: boolean
}

const HeaderLogoSectionContent: FC<
  React.PropsWithChildren<HeaderLogoSectionProps>
> = ({ onShowDrawerMobile, onHideDrawerMobile, inBurgerMenu = false }) => {
  const dispatch = useAppDispatch()
  const { isIpadLandscape } = useDeviceDetect()
  const { isSearchFocused, setIsSearchFocused } = useSearchContext()
  const { t } = useTranslation()

  const isFetching = useAppSelector(selectIsFetchingCustomerAddress)
  const totalQuantity = useAppSelector(selectTotalQuantity)
  const logo = useAppSelector(selectLogo)
  const hasSessionBasedShoppingCart = useAppSelector(
    selectHasSessionBasedShoppingCart,
  )
  const isCxmlProcurementUser = useAppSelector(
    selectIsProcurementTechnicalUserCxml,
  )
  const allowDirectShopCheckout = useAppSelector(selectAllowDirectShopCheckout)
  const canReturnToProcurement = isCxmlProcurementUser && !allowDirectShopCheckout
  const { onReturnToProcurement } = usePunchoutContext() || {}

  const handleClickOnLogo = useCallback(() => {
    dispatch(setSearchTerm(''))
  }, [dispatch])

  const handleSearchFocus = useCallback(() => {
    if (inBurgerMenu) {
      onHideDrawerMobile()
    }
    setIsSearchFocused(true)
  }, [inBurgerMenu, onHideDrawerMobile, setIsSearchFocused])

  const handleSearchBlur = useCallback(() => {
    setIsSearchFocused(false)
  }, [setIsSearchFocused])

  const handleReturnToProcurementSystem = useCallback(() => {
    if (
      canReturnToProcurement
      && typeof onReturnToProcurement === 'function'
    ) {
      onReturnToProcurement()
    }
  }, [canReturnToProcurement, onReturnToProcurement])

  return (
    <div className="header__bottom__container">
      <div className="header__bottom">
        <div className="header__bottom__container__menu">
          <div className="header__logo">
            <B2BecLink to="/my-workspace" onClick={handleClickOnLogo}>
              <img alt="Kaercher Logo" className="kaercher-logo" src={logo} />
            </B2BecLink>
          </div>

          {!isIpadLandscape && <HeaderMenu />}

          <div className="header__bottom__container__menu__icons">
            {canReturnToProcurement && (
              <div className="header__return-to-purchase-system">
                <B2becButton
                  type="button"
                  onClick={handleReturnToProcurementSystem}
                >
                  {t('returnToPurchaseSystemButton')}
                </B2becButton>
              </div>
            )}
            <div className="header__user-section__button menu-item--small header-submenu-container relative">
              <UserInfoSection isFetching={isFetching} />
            </div>

            <div
              className="header__user-section__button menu-item--small"
              style={{
                position: 'relative',
              }}
            >
              <PermissionWrapper
                permission={MODULE_SHOP_NAVIGATION_RIGHTS.VIEW_CART}
              >
                <CartInfoSection totalQuantity={totalQuantity} />
              </PermissionWrapper>
            </div>

            {isIpadLandscape && (
              <>
                <div className="header__user-section__button menu-item--small">
                  {isSearchFocused
                    ? (
                      <CloseIcon
                        className="header__user-section__button__icon"
                        onClick={handleSearchBlur}
                      />
                    )
                    : (
                      <SearchIcon
                        className="header__user-section__button__icon"
                        onClick={handleSearchFocus}
                      />
                    )}
                </div>
                {!inBurgerMenu && (
                  <div className="header__user-section__button menu-item--small">
                    <BurgerMenuIcon
                      className="header__user-section__button__icon menu-burger-icon"
                      onClick={onShowDrawerMobile}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const HeaderLogoSection: FC<React.PropsWithChildren<HeaderLogoSectionProps>> = (
  props,
) => {
  return <HeaderLogoSectionContent {...props} />
}

export default HeaderLogoSection
